import { baseApiAuthenticated } from '@/api/v2/index';
import { urlBuilder } from '@/api/utils';
import { fetchAuthWrapper } from '@starjack/api';
import { MailText } from '@/models/mailConfiguration';
import { LogoType } from '@/pageContents/mailConfiguration/mailGraphics/LogoUploadContainer';

const cleanNullValues = (mailText: MailText) => ({
    ...mailText,
    groups: mailText.groups.map((group) => ({
        ...group,
        variables: group.variables.map((variable) => ({
            ...variable,
            texts: variable.texts.map((text) => ({
                ...text,
                customText: text.customText || '',
            })),
        })),
    })),
});

export const mailConfigurationUrls = () => ({
    mailLogo: urlBuilder('order', `mailimages`),
    mailText: urlBuilder('order', 'mailtexts'),
});

export const mailConfigurationAPi = baseApiAuthenticated.injectEndpoints({
    endpoints: (builder) => ({
        getMailTexts: builder.query<MailText, void>({
            query: () => mailConfigurationUrls().mailText,
        }),

        postMailText: builder.mutation<MailText, MailText>({
            query: (mailText) => ({
                url: mailConfigurationUrls().mailText,
                method: 'POST',
                body: cleanNullValues(mailText),
            }),
        }),
    }),
});

// Requests outside redux store
/**
 * Returns logo as Object url
 */
export const getMailLog = async (logo: LogoType): Promise<File> => {
    const response = await fetchAuthWrapper(
        `${mailConfigurationUrls().mailLogo}/${logo}`,
        {
            headers: {
                'Content-Type': '',
                Accept: '',
            },
        }
    );

    if (response.ok) {
        try {
            const blob = await response.blob();

            return blob.size ? new File([blob], logo) : null;
        } catch (e) {
            console.error(e);
            return null;
        }
    }

    return null;
};

export const postMailLogo = async (
    logo: LogoType,
    file: File
): Promise<boolean> => {
    const response = await fetchAuthWrapper(
        `${mailConfigurationUrls().mailLogo}/${logo}`,
        {
            method: 'POST',
            body: file,
        }
    );

    return response.ok;
};

export const deleteMailLogo = async (logo: LogoType): Promise<boolean> => {
    const response = await fetchAuthWrapper(
        `${mailConfigurationUrls().mailLogo}/${logo}`,
        {
            method: 'DELETE',
        }
    );

    return response.ok;
};

export const { useGetMailTextsQuery, usePostMailTextMutation } =
    mailConfigurationAPi;
